import { useState, type ReactNode } from 'react';

import { addInstrument, addPattern, beatsPerBar, timeSignatureOf, type SoundInstrument } from '../../lib/sound-doc.js';
import { useSound, type SoundSelection } from '../../state/sound.js';
import { ContextMenu, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { MuteSolo } from './Timeline.js';

/**
 * Document outline: layers, tracks (with clips), instruments, patterns,
 * voice lines, master and return buses. Click selects (the inspector shows
 * it), right-click for duplicate/delete, + adds.
 */

const NEW_INSTRUMENTS: Array<{ id: string; label: string; def: SoundInstrument }> = [
  { id: 'piano', label: 'Piano (General MIDI)', def: { type: 'soundfont', bank: 0, program: 0 } },
  { id: 'strings', label: 'Strings (General MIDI)', def: { type: 'soundfont', bank: 0, program: 48 } },
  { id: 'bass', label: 'Bass (General MIDI)', def: { type: 'soundfont', bank: 0, program: 33 } },
  { id: 'drums', label: 'Drums (General MIDI kit)', def: { type: 'soundfont', bank: 128, program: 0 } },
  { id: 'lead', label: 'Synth lead', def: { type: 'synth', voices: 8, source: { type: 'osc', wave: 'supersaw' }, amp: { adsr: [0.01, 0.2, 0.7, 0.3] } } },
  { id: 'pad', label: 'Synth pad', def: { type: 'synth', voices: 8, source: { type: 'osc', wave: 'saw' }, amp: { adsr: [0.6, 0.5, 0.8, 1.2] }, effects: [{ type: 'filter', mode: 'lowpass', cutoff: 1800 }, { type: 'chorus' }] } },
  { id: 'pluck', label: 'Pluck (string model)', def: { type: 'synth', voices: 8, source: { type: 'pluck', decay: 2, brightness: 0.6 }, amp: { adsr: [0.001, 0.1, 1, 0.3] } } },
  { id: 'kit', label: 'Synth drum kit', def: { type: 'drumkit', pads: { '36': { source: { type: 'membrane', size: 0.6, tension: 0.3 } }, '38': { source: { type: 'noise', color: 'white' } } } } },
  { id: 'choir', label: 'Singer (choir)', def: { type: 'singer', voice: 'soprano' } },
];

interface ItemProps {
  sel: SoundSelection | null;
  me: SoundSelection;
  icon: IconName;
  label: ReactNode;
  meta?: ReactNode;
  depth?: number;
  muted?: boolean;
  extra?: ReactNode;
  onMenu?: (x: number, y: number) => void;
  onActivate?: () => void;
}

function same(a: SoundSelection | null, b: SoundSelection): boolean {
  return !!a && JSON.stringify(a) === JSON.stringify(b);
}

function Item({ sel, me, icon, label, meta, depth = 0, muted, extra, onMenu, onActivate }: ItemProps) {
  const isSel = same(sel, me);
  return (
    <li
      role="treeitem"
      aria-selected={isSel}
      tabIndex={isSel ? 0 : -1}
      className={`tree-item snd-outline-item${isSel ? ' is-selected' : ''}${muted ? ' is-hidden' : ''}`}
      style={{ paddingLeft: 8 + depth * 14 }}
      onClick={() => useSound.getState().select(me)}
      onDoubleClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onActivate?.();
      }}
      onContextMenu={(e) => {
        if (!onMenu) return;
        e.preventDefault();
        useSound.getState().select(me);
        onMenu(e.clientX, e.clientY);
      }}
    >
      <Icon name={icon} size={13} className="tree-kind" />
      <span className="tree-label">{label}</span>
      {meta && <span className="tree-meta">{meta}</span>}
      {extra}
    </li>
  );
}

function Heading({ title, count, add, addTitle }: { title: string; count?: number; add?: (() => void) | MenuItem[]; addTitle?: string }) {
  return (
    <li className="snd-outline-head" role="presentation">
      <span>{title}</span>
      {count !== undefined && <span className="muted">{count}</span>}
      <span className="bar-spacer" />
      {typeof add === 'function' && (
        <button type="button" className="ibtn" title={addTitle} aria-label={addTitle} onClick={add}>
          <Icon name="plus" size={13} />
        </button>
      )}
      {Array.isArray(add) && (
        <MenuButton className="ibtn" title={addTitle} items={add}>
          <Icon name="plus" size={13} />
        </MenuButton>
      )}
    </li>
  );
}

export function SoundOutline() {
  const doc = useSound((s) => s.doc);
  const sel = useSound((s) => s.selection);
  const solo = useSound((s) => s.solo);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  if (!doc) return null;
  const s = useSound.getState();
  const openMenu = (x: number, y: number) => setMenu({ x, y });
  const menuItems: MenuItem[] = [
    { id: 'dup', label: 'Duplicate', icon: 'copy', shortcut: 'Ctrl+D', run: () => s.duplicateSelection() },
    { id: 'del', label: 'Delete', icon: 'trash', shortcut: 'Del', danger: true, run: () => s.deleteSelection() },
  ];
  const showLayers = doc.kind === 'sfx' || !!doc.layers?.length;
  const showSong = doc.kind === 'song' || doc.kind === 'instrument' || !!doc.tracks?.length || !!doc.patterns;
  const showVoice = doc.kind === 'voice' || !!doc.voice;
  const instruments = Object.entries(doc.instruments ?? {});
  const patterns = Object.entries(doc.patterns ?? {});

  return (
    <div className="hierarchy snd-outline">
      <ul className="tree-list" role="tree" aria-label="Sound outline">
        <Item sel={sel} me={{ kind: 'doc' }} icon="settings" label={doc.name || 'Sound'} meta={doc.kind} />

        {showLayers && (
          <>
            <Heading title="Layers" count={doc.layers?.length ?? 0} add={() => s.addLayer()} addTitle="Add layer" />
            {(doc.layers ?? []).map((l, i) => (
              <Item
                key={`l${i}`}
                sel={sel}
                me={{ kind: 'layer', index: i }}
                icon="layers"
                label={l.name || `layer ${i + 1}`}
                meta={l.source?.type}
                muted={l.mute}
                onMenu={openMenu}
                extra={<MuteSolo muted={!!l.mute} soloed={solo.includes(`layer:${i}`)} onMute={() => s.toggleMute(`layer:${i}`)} onSolo={() => s.toggleSolo(`layer:${i}`)} label={l.name ?? 'layer'} />}
              />
            ))}
          </>
        )}

        {showSong && (
          <>
            <Heading title="Tracks" count={doc.tracks?.length ?? 0} add={() => s.addTrack()} addTitle="Add track" />
            {(doc.tracks ?? []).map((t, i) => (
              <li key={`t${i}`} role="none" className="snd-outline-group">
                <ul role="group">
                  <Item
                    sel={sel}
                    me={{ kind: 'track', index: i }}
                    icon="audio"
                    label={t.name || `Track ${i + 1}`}
                    meta={typeof t.instrument === 'string' ? t.instrument : undefined}
                    muted={t.mute}
                    onMenu={openMenu}
                    extra={<MuteSolo muted={!!t.mute} soloed={solo.includes(`track:${i}`)} onMute={() => s.toggleMute(`track:${i}`)} onSolo={() => s.toggleSolo(`track:${i}`)} label={t.name ?? 'track'} />}
                  />
                  {(t.clips ?? []).map((c, ci) => (
                    <Item
                      key={ci}
                      sel={sel}
                      me={{ kind: 'clip', track: i, clip: ci }}
                      icon="file"
                      depth={1}
                      label={typeof c.pattern === 'string' ? c.pattern : '(inline)'}
                      meta={`bar ${String(c.start ?? 0)}${c.repeat && c.repeat > 1 ? ` ×${c.repeat}` : ''}`}
                      onMenu={openMenu}
                      onActivate={() => typeof c.pattern === 'string' && s.openPattern(c.pattern)}
                    />
                  ))}
                </ul>
              </li>
            ))}
            <Heading
              title="Instruments"
              count={instruments.length + (doc.instrument ? 1 : 0)}
              addTitle="Add instrument"
              add={NEW_INSTRUMENTS.map((n) => ({
                id: n.id,
                label: n.label,
                run: () => {
                  const cur = useSound.getState().doc;
                  if (!cur) return;
                  const r = addInstrument(cur, n.id, structuredClone(n.def));
                  s.update(r.doc, { kind: 'instrument', name: r.name });
                },
              }))}
            />
            {doc.instrument && <Item sel={sel} me={{ kind: 'instrument', name: '' }} icon="audio" label="(this instrument)" meta={doc.instrument.type} />}
            {instruments.map(([name, def]) => (
              <Item key={`i${name}`} sel={sel} me={{ kind: 'instrument', name }} icon="audio" label={name} meta={def?.type} onMenu={openMenu} />
            ))}
            <Heading
              title="Patterns"
              count={patterns.length}
              addTitle="Add pattern"
              add={[
                { id: 'notes', label: 'Note pattern', icon: 'pencil', run: () => s.addPattern() },
                {
                  id: 'drums',
                  label: 'Drum pattern',
                  icon: 'grid',
                  run: () => {
                    const cur = useSound.getState().doc;
                    if (!cur) return;
                    const r = addPattern(cur, 'drums', { length: beatsPerBar(timeSignatureOf(cur)), drums: { kick: 'x...x...x...x...', snare: '....x.......x...', closedHat: 'x.x.x.x.x.x.x.x.' } });
                    s.update(r.doc, { kind: 'pattern', name: r.name });
                    s.openPattern(r.name, 'steps');
                  },
                },
              ]}
            />
            {patterns.map(([name, p]) => (
              <Item
                key={`p${name}`}
                sel={sel}
                me={{ kind: 'pattern', name }}
                icon={p?.drums ? 'grid' : 'pencil'}
                label={name}
                meta={p?.notes?.length ? `${p.notes.length} notes` : p?.drums ? `${Object.keys(p.drums).length} lanes` : undefined}
                onMenu={openMenu}
                onActivate={() => s.openPattern(name)}
              />
            ))}
          </>
        )}

        {showVoice && (
          <>
            <Heading title="Voice" count={doc.voice?.lines?.length ?? 0} add={() => s.addVoiceLine()} addTitle="Add voice line" />
            <Item
              sel={sel}
              me={{ kind: 'voice' }}
              icon="send"
              label={doc.voice?.mode === 'sing' ? 'Singing voice' : 'Speech voice'}
              meta={doc.voice?.voice}
              extra={<MuteSolo muted={false} soloed={solo.includes('voice')} onSolo={() => s.toggleSolo('voice')} label="voice" />}
            />
            {(doc.voice?.lines ?? []).map((line, i) => (
              <Item key={`v${i}`} sel={sel} me={{ kind: 'voiceLine', index: i }} icon="file" depth={1} label={`“${line.text}”`} onMenu={openMenu} />
            ))}
          </>
        )}

        <Heading title="Mix" />
        <Item sel={sel} me={{ kind: 'master' }} icon="audio" label="Master" meta={doc.master?.effects?.length ? `${doc.master.effects.length} fx` : undefined} />
        {Object.keys(doc.master?.returns ?? {}).map((name) => (
          <Item key={`r${name}`} sel={sel} me={{ kind: 'return', name }} icon="refresh" depth={1} label={name} meta="return" onMenu={openMenu} />
        ))}
      </ul>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />}
    </div>
  );
}
